import React, { useState } from 'react'
import {
  createCaptureRules,
  deleteTimeSlots,
} from '../../../../../api/commonApi'
import {
  getHttpsDomain,
  isEmailActivityItem,
  isRunningClocking,
  isWebActivityItem,
} from '../../../../components/common/sourceIcons'
import {
  RECOVER_ACTION_TOOLTIP,
  isNoiseItem,
} from '../../../../components/common/timeNoiseUtils'
import { DeleteConfirmDialog } from '@components/common/dialog/DeleteConfirmDialog'
import { ExcludeConfirmDialog } from '@components/common/dialog/ExcludeConfirmDialog'
// 14px black delete icon (design asset).
import DELETE_ICON from '@public/assets/action/icon_delete_black.svg'
import DELETE_DISABLED_ICON from '@public/assets/action/icon_delete_disabled.svg'
import RECOVER_ICON from '@public/assets/button/icon_recover.svg'
import EMAIL_EXCLUDE_ICON from '@public/assets/settings/email_default.svg'
import EMAIL_EXCLUDE_DISABLED_ICON from '@public/assets/settings/email_disable.svg'
import EDIT_ICON from '@public/assets/common/icon_editable_black.svg'
import URL_EXCLUDE_ICON from '@public/assets/url-exclude.svg'
import {
  ActionMenu,
  MenuAction,
} from '../../../../components/common/action/ActionMenu'
import { RestoreActivitiesDialog } from './RestoreActivitiesDialog'
import {
  getAssociatedActivitySlots,
  getRelatedTimeEntryId,
  getRestoreSourceName,
} from '../../utils/relatedTimeEntry'
import {
  getCurrentUserId,
  shouldUpdateEmailExclusionList,
} from '@/utils/currentUser'
import { ActivityActionItem } from '@/types/activity'
import type {
  RenderDeleteSlotProps,
  TimelineSlotDeleteRefreshOptions,
} from '../../types'
import { useConnectedAppsStore } from '@/stores/useConnectedAppsStore'
import {
  getUnexcludedWebUrls,
  getWebWhitelistState,
  normalizeWebExclusionValue,
} from '@/utils/webCaptureSettings'
import { normalizeServiceExclusionRules } from '@/features/settings/utils/exclusionUtils'
import { useWebExclusionStore } from '@/stores/useWebExclusionStore'
import { confirmTimeEntryDeletion } from '@/stores/useDeleteTimeEntryConfirmationStore'
import { useNotification } from '@/matteroomTab/Notification/NotificationContext'

export function RenderDeleteSlot({
  activeTab,
  disabled = false,
  emailExclusionList = [],
  groupSlots,
  handleActionDelete,
  isMobile = false,
  item,
  onEdit,
  onDeleteSlotSuccess,
  onNoiseChange,
  selectedUser,
  setEmailExclusionList,
  showBlockAction = true,
}: RenderDeleteSlotProps) {
  const [loading, setLoading] = useState(false)
  const [showExcludeDialog, setShowExcludeDialog] = useState(false)
  const [pendingExcludeItem, setPendingExcludeItem] =
    useState<ActivityActionItem | null>(null)
  const [excluding, setExcluding] = useState(false)
  const [showGroupDeleteConfirm, setShowGroupDeleteConfirm] = useState(false)
  const [showRestoreActivitiesDialog, setShowRestoreActivitiesDialog] =
    useState(false)
  const services = useConnectedAppsStore(state => state.services)
  const notification = useNotification()
  const webExclusionValues = useWebExclusionStore(state => state.values)
  const addWebExclusionValues = useWebExclusionStore(state => state.addValues)
  const isGroup = Array.isArray(groupSlots)
  const { enabled: webWhitelistEnabled, exclusionList } =
    getWebWhitelistState(services)
  const slots = groupSlots || []
  const interactionDisabled =
    disabled ||
    (isGroup ? slots.some(isRunningClocking) : isRunningClocking(item))
  const deleteEnabled =
    (isGroup ? slots.every(slot => slot.canEdit) : item.canEdit) &&
    !interactionDisabled
  const senders = Array.isArray(item.relateTimeEntry?.metadata?.from)
    ? item.relateTimeEntry.metadata.from.filter(
        sender => sender && sender !== item.tkpr_username
      )
    : []
  const excludedSenders = emailExclusionList.map(({ value }) => value)
  const activeSenders = senders.filter(
    sender => !excludedSenders.includes(sender)
  )
  const isExcluded = senders.length > 0 && activeSenders.length === 0
  const urls = (item.external_links || [])
    .map(link => getHttpsDomain(link.url))
    .filter(Boolean)
    .filter((url, index, values) => values.indexOf(url) === index)
  const excludedUrls = new Set([
    ...webExclusionValues,
    ...normalizeServiceExclusionRules(exclusionList)
      .map(rule => normalizeWebExclusionValue(rule.value || ''))
      .filter((value): value is string => Boolean(value)),
  ])
  const activeUrls = getUnexcludedWebUrls(urls, excludedUrls)
  const isUrlExcluded = urls.length > 0 && activeUrls.length === 0
  const itemIsFiltered = isGroup ? slots.some(isNoiseItem) : isNoiseItem(item)
  const restoreTarget = isGroup ? slots : item

  const deleteActivitySlots = async (
    slotIds: Array<string | number>,
    refreshOptions?: TimelineSlotDeleteRefreshOptions
  ) => {
    if (slotIds.length === 0) return
    const response = await deleteTimeSlots({ ids: slotIds })
    if (response.status < 200 || response.status >= 300) {
      throw new Error('Unable to delete the selected activity.')
    }
    await onDeleteSlotSuccess(refreshOptions)
  }

  const deleteSlots = async () => {
    setLoading(true)
    try {
      await deleteActivitySlots((isGroup ? slots : [item]).map(({ id }) => id))
    } catch (error) {
      console.error('Failed to delete time slot:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const restore = async (restoreAll = false) => {
    await onNoiseChange?.(
      restoreAll ? getAssociatedActivitySlots(item) : restoreTarget,
      false
    )
  }

  const handleExclude = async (
    deleteOutstanding: boolean,
    selectedValues: string[],
    targetType: 'email' | 'url' = 'email',
    targetItem = pendingExcludeItem
  ) => {
    if (!targetItem) return
    const userId = getCurrentUserId(selectedUser)
    if (userId == null) return
    if (targetType === 'url') {
      setExcluding(true)
      try {
        await confirmTimeEntryDeletion(
          1,
          async () => {
            await Promise.all(
              selectedValues.map(value =>
                createCaptureRules({
                  action: 'block',
                  del_historic_data: deleteOutstanding,
                  scope_type: 'user',
                  target_type: targetType,
                  user: Number(userId),
                  value,
                })
              )
            )
            addWebExclusionValues(selectedValues)

            // URL exclusion from a slot menu targets this activity only. Routing
            // through handleActionDelete would turn the slot into its parent
            // time-entry ID and remove every activity attached to that entry.
            const response = await deleteTimeSlots({ ids: [targetItem.id] })
            if (response.status < 200 || response.status >= 300) {
              throw new Error('Unable to delete the selected activity.')
            }
            await onDeleteSlotSuccess()
          },
          { entity: 'activity' }
        )
      } catch (error) {
        console.error(`Failed to exclude ${targetType}:`, error)
        notification?.showNotification({
          type: 'error',
          message:
            (error as Error)?.message || 'Unable to exclude this activity.',
        })
      } finally {
        setShowExcludeDialog(false)
        setPendingExcludeItem(null)
        setExcluding(false)
      }
      return
    }

    setExcluding(true)
    try {
      await Promise.all(
        selectedValues.map(value =>
          createCaptureRules({
            action: 'block',
            del_historic_data: deleteOutstanding,
            scope_type: 'user',
            target_type: targetType,
            user: Number(userId),
            value,
          })
        )
      )
      if (
        targetType === 'email' &&
        shouldUpdateEmailExclusionList(selectedUser)
      ) {
        setEmailExclusionList?.(current => [
          ...current,
          ...selectedValues.map(value => ({ value })),
        ])
      }
      if (targetType === 'email') {
        // An email exclusion from a slot menu targets that activity. Routing
        // through handleActionDelete would turn the slot into its parent
        // time-entry ID and remove every activity attached to that entry.
        await deleteActivitySlots([targetItem.id], {
          refreshAll: deleteOutstanding,
        })
        return
      }
      if (!handleActionDelete) {
        throw new Error('Unable to delete this activity.')
      }
      await handleActionDelete(targetItem, {
        skipConfirmation: targetType === 'email',
        deleteOutstanding,
        excludedSenders: selectedValues,
      })
    } catch (error) {
      console.error(`Failed to exclude ${targetType}:`, error)
      if (targetType === 'email') {
        notification?.showNotification({
          type: 'error',
          message: 'Unable to delete this activity. Refresh and try again.',
        })
        try {
          await onDeleteSlotSuccess({ refreshAll: true, refreshOnly: true })
        } catch {
          // Keep the original exclusion error.
        }
      }
    } finally {
      setShowExcludeDialog(false)
      setPendingExcludeItem(null)
      setExcluding(false)
    }
  }

  const shouldConfirmRestore =
    activeTab === 'work_activities' &&
    !isGroup &&
    itemIsFiltered &&
    Boolean(getRelatedTimeEntryId(item))
  const actions: MenuAction[] = [
    ...(onEdit
      ? [
          {
            enabled: true,
            icon: EDIT_ICON,
            id: 'edit',
            onClick: () => onEdit(item),
            text: 'Edit Details',
          },
        ]
      : []),
    {
      enabled: Boolean(deleteEnabled) && !loading,
      icon: deleteEnabled && !loading ? DELETE_ICON : DELETE_DISABLED_ICON,
      id: 'delete',
      loading,
      onClick: () => setShowGroupDeleteConfirm(true),
      text: 'Delete',
    },
    ...(!isGroup &&
    showBlockAction &&
    isEmailActivityItem(item) &&
    senders.length > 0
      ? [
          {
            enabled: !isExcluded && !interactionDisabled && !excluding,
            icon:
              isExcluded || interactionDisabled
                ? EMAIL_EXCLUDE_DISABLED_ICON
                : EMAIL_EXCLUDE_ICON,
            id: 'exclude',
            onClick: () => {
              setPendingExcludeItem(item)
              setShowExcludeDialog(true)
            },
            position: 'left' as const,
            text: 'Block',
            tooltip:
              "Exclude this sender's inbound emails from activity tracking and delete this activity record.",
          },
        ]
      : []),
    ...(!webWhitelistEnabled &&
    !isGroup &&
    isWebActivityItem(item) &&
    urls.length > 0
      ? [
          {
            enabled: !isUrlExcluded && !interactionDisabled && !excluding,
            icon: URL_EXCLUDE_ICON,
            id: 'exclude_url',
            onClick: () => void handleExclude(false, activeUrls, 'url', item),
            position: 'left' as const,
            text: 'Exclude',
            tooltip:
              'Exclude this website from tracking and delete this activity record.',
          },
        ]
      : []),
    ...(onNoiseChange && itemIsFiltered
      ? [
          {
            enabled: !interactionDisabled,
            icon: RECOVER_ICON,
            id: 'recover',
            position: 'left' as const,
            onClick: () =>
              shouldConfirmRestore
                ? setShowRestoreActivitiesDialog(true)
                : restore(),
            text: 'Restore',
            tooltip: RECOVER_ACTION_TOOLTIP,
          },
        ]
      : []),
  ]

  return (
    <>
      <ActionMenu
        actions={actions}
        className={`btn-more${isMobile ? 'btn-more-mobile' : ''}`}
        iconSize={14}
        isMobile={isMobile}
      />
      {showExcludeDialog && (
        <ExcludeConfirmDialog
          isMobile={isMobile}
          loading={excluding}
          sender={activeSenders}
          onClose={() => {
            setShowExcludeDialog(false)
            setPendingExcludeItem(null)
          }}
          onConfirm={handleExclude}
        />
      )}
      {showGroupDeleteConfirm && (
        <DeleteConfirmDialog
          isMobile={isMobile}
          count={isGroup ? slots.length : 1}
          title={undefined}
          description={undefined}
          onClose={() => setShowGroupDeleteConfirm(false)}
          onConfirm={deleteSlots}
        />
      )}
      {showRestoreActivitiesDialog && (
        <RestoreActivitiesDialog
          isMobile={isMobile}
          sourceName={getRestoreSourceName(item)}
          onClose={() => setShowRestoreActivitiesDialog(false)}
          onRestoreOne={async () => {
            setShowRestoreActivitiesDialog(false)
            await restore(false)
          }}
          onRestoreAll={async () => {
            setShowRestoreActivitiesDialog(false)
            await restore(true)
          }}
        />
      )}
    </>
  )
}
